import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { DealStatusView } from '../../shared/models';

interface SubscriptionContractActionButtonProps {
	agreementFilename: string | undefined;
	view: DealStatusView | undefined;
	downloadUrl: string;
	onSendContract: () => void;
	onViewStatus: () => void;
	onUploadContract: () => void;
	onChangeReminderFrequency: () => void;
	onViewPriorAgreements: () => void;
}

type PageActionItem = keyof typeof PageActionItemMap;
const PageActionItemMap = {
	Download: 'Download Contract',
	Send: 'Send For Signature',
	Status: 'View Status',
	UploadContract: 'Upload Modified Contract',
	ChangeReminderFrequency: 'Change Reminder Frequency',
	ViewPriorAgreements: 'View Prior Agreements',
};

export const SubscriptionContractActionButton: React.FC<SubscriptionContractActionButtonProps> = ({
	agreementFilename,
	view,
	downloadUrl,
	onSendContract,
	onViewStatus,
	onUploadContract,
	onViewPriorAgreements,
	onChangeReminderFrequency
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: PageActionItem) => {
		switch (action) {
			case 'Download':
				location.href = downloadUrl;
				break;
			case 'Send':
				onSendContract();
				break;
			case 'Status':
				onViewStatus();
				break;
			case 'UploadContract':
				onUploadContract();
				break;
			case 'ChangeReminderFrequency':
				onChangeReminderFrequency();
				break;
			case 'ViewPriorAgreements':
				onViewPriorAgreements();
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
			return;
		}
		setOpen(false);
	};

	const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, actionItem: PageActionItem) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Contract Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									{showContractButtons(view) && !agreementFilename && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Send')}>
											{PageActionItemMap['Send']}
										</MenuItem>
									)}
									{!agreementFilename &&
										<MenuItem onClick={event => handleMenuItemClick(event, 'Download')}>
											{PageActionItemMap['Download']}
										</MenuItem>
									}
									{!!view?.eSignDocument && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Status')}>
											{PageActionItemMap['Status']}
										</MenuItem>
									)}
									{showContractButtons(view) && !agreementFilename &&(
										<MenuItem onClick={event => handleMenuItemClick(event, 'UploadContract')}>
											{PageActionItemMap['UploadContract']}
										</MenuItem>
									)}
									{!!view?.eSignDocument && showContractButtons(view) && !agreementFilename && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ChangeReminderFrequency')}>
											{PageActionItemMap['ChangeReminderFrequency']}
										</MenuItem>
									)}
									{!!view?.eSignDocuments && view?.eSignDocuments.length > 0 && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ViewPriorAgreements')}>
											{PageActionItemMap['ViewPriorAgreements']}
										</MenuItem>
									)}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};

function showContractButtons(data?: DealStatusView) {
	return data?.eSignDocument?.state != 'Completed' && data?.eSignDocument?.state != 'Cancelled';
}